import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ArrowLeft, Plus, Truck, PauseCircle } from "lucide-react";
import {
  MENU as DEFAULT_MENU,
  CATEGORY_ORDER as DEFAULT_ORDER,
  FLAVOR_OPTIONS as DEFAULT_FLAVORS,
  SODA_OPTIONS as DEFAULT_SODAS,
  DIP_OPTIONS as DEFAULT_DIPS,
  COMBO_PRICE as DEFAULT_COMBO_PRICE,
  money,
} from "../lib/menu";
import { useCart } from "../context/CartContext";
import ItemDialog from "../components/order/ItemDialog";
import CartSheet from "../components/order/CartSheet";
import { LINKS } from "../data";
import { api } from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const startPrice = (item) => (item.variants ? Math.min(...item.variants.map((v) => v.price)) : item.price);

export default function Order() {
  const { count, setOpen, totalCents } = useCart();
  const [active, setActive] = useState(null);
  const [orderingEnabled, setOrderingEnabled] = useState(true);
  const [menu, setMenu] = useState(DEFAULT_MENU);
  const [categoryOrder, setCategoryOrder] = useState(DEFAULT_ORDER);
  const [flavors, setFlavors] = useState(DEFAULT_FLAVORS);
  const [sodas, setSodas] = useState(DEFAULT_SODAS);
  const [dips, setDips] = useState(DEFAULT_DIPS);
  const [comboPrice, setComboPrice] = useState(DEFAULT_COMBO_PRICE);
  const [images, setImages] = useState({});
  const kiosk = typeof window !== "undefined" && localStorage.getItem("kiosk_mode") === "1";

  useEffect(() => {
    api.get("/store/status").then(({ data }) => setOrderingEnabled(data.ordering_enabled)).catch(() => {});
    api.get("/menu").then(({ data }) => {
      if (data?.menu) setMenu(data.menu);
      if (Array.isArray(data?.categoryOrder)) setCategoryOrder(data.categoryOrder);
      if (Array.isArray(data?.flavors) && data.flavors.length) setFlavors(data.flavors);
      if (Array.isArray(data?.sodas) && data.sodas.length) setSodas(data.sodas);
      if (Array.isArray(data?.dips) && data.dips.length) setDips(data.dips);
      if (Number.isFinite(data?.comboPrice)) setComboPrice(data.comboPrice);
      if (data?.images) setImages(data.images);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/95 backdrop-blur-xl border-b border-neon-green/20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Link to={kiosk ? "/kiosk" : "/"} data-testid="back-home" className="flex items-center gap-2 text-neutral-300 hover:text-neon-green transition-colors">
            <ArrowLeft size={20} />
            <span className="font-heading text-2xl text-neon-green">67</span>
            <span className="font-heading text-xl text-neon-yellow tracking-wide">{kiosk ? "START OVER" : "WINGS"}</span>
          </Link>
          <button onClick={() => setOpen(true)} data-testid="open-cart-btn" className="relative inline-flex items-center gap-2 font-heading text-lg bg-neon-green text-black px-5 py-2 hover:bg-neon-yellow transition-colors">
            <ShoppingBag size={18} /> Cart
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-neon-yellow text-black rounded-full h-6 min-w-6 px-1 flex items-center justify-center font-body text-xs font-bold">{count}</span>
            )}
          </button>
        </div>
      </header>

      {/* Title */}
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 pt-12 pb-6">
        <span className="font-graffiti text-neon-yellow text-4xl -rotate-2 inline-block">order online · pickup</span>
        <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl text-neon-green uppercase tracking-tighter leading-none neon-glow-green">Build Your Order</h1>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <p className="font-body text-neutral-400 max-w-lg">Pick your wings, choose your flavors, pay online & skip the line. Ready for pickup at 10 Simas St, Salinas.</p>
          {!kiosk && (
            <a href={LINKS.doordash} target="_blank" rel="noopener noreferrer" data-testid="delivery-btn" className="inline-flex items-center gap-2 font-heading text-lg border border-neon-yellow text-neon-yellow px-4 py-2 hover:bg-neon-yellow hover:text-black transition-colors">
              <Truck size={18} /> Want Delivery? Order on DoorDash
            </a>
          )}
        </div>
      </div>

      {!orderingEnabled && (
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 mb-4">
          <div data-testid="ordering-closed-banner" className="flex items-center gap-3 border border-neon-yellow bg-neon-yellow/10 text-neon-yellow p-5">
            <PauseCircle size={26} className="shrink-0" />
            <p className="font-heading text-xl sm:text-2xl uppercase tracking-wide">
              Online ordering is paused right now — check back soon! You can still order on DoorDash for delivery.
            </p>
          </div>
        </div>
      )}

      {/* Menu */}
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-40 space-y-16">
        {categoryOrder.map((key) => {
          const cat = menu[key];
          if (!cat) return null;
          const visibleItems = (cat.items || []).filter((it) => it.enabled !== false);
          if (visibleItems.length === 0) return null;
          return (
            <section key={key} id={key}>
              <div className="flex items-end gap-3 mb-6">
                <h2 className="font-heading text-4xl sm:text-5xl text-neon-yellow uppercase tracking-tighter leading-none">{cat.title}</h2>
                <span className="font-graffiti text-neon-green text-2xl -rotate-2">{cat.tag}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleItems.map((item) => {
                  const rawImg = images[item.id];
                  const img = rawImg && rawImg.startsWith("/") ? `${API_BASE}${rawImg}` : rawImg;
                  const soldOut = !!item.sold_out;
                  const disabled = !orderingEnabled || soldOut;
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => !disabled && setActive(item)}
                      disabled={disabled}
                      data-testid={`menu-card-${item.id}`}
                      whileHover={!disabled ? { y: -3 } : {}}
                      className={`group text-left bg-elevated border transition-colors flex flex-col overflow-hidden relative ${
                        soldOut ? "border-neon-yellow/30 opacity-70 cursor-not-allowed" : "border-neon-green/20 hover:border-neon-green disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-neon-green/20"
                      }`}
                    >
                      {soldOut && (
                        <span data-testid={`soldout-badge-${item.id}`} className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 font-heading text-xs uppercase tracking-widest bg-neon-yellow text-black px-2 py-1 shadow-[0_0_14px_rgba(255,255,0,0.4)]">
                          Sold Out
                        </span>
                      )}
                      {img && (
                        <div className="w-full aspect-[4/3] bg-black overflow-hidden">
                          <img
                            src={img}
                            alt={item.name}
                            className={`w-full h-full object-cover transition-transform duration-500 ${soldOut ? "grayscale" : "group-hover:scale-[1.03]"}`}
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className={`font-heading text-2xl sm:text-3xl uppercase tracking-wide transition-colors ${soldOut ? "text-neutral-500" : "text-white group-hover:text-neon-green"}`}>{item.name}</h3>
                          <span className="font-heading text-2xl text-neon-yellow whitespace-nowrap">
                            {item.variants ? `from ${money(startPrice(item))}` : money(item.price)}
                          </span>
                        </div>
                        <p className="font-body text-sm text-neutral-400 mt-2 flex-1">{item.desc}</p>
                        {soldOut ? (
                          <span className="mt-4 inline-flex items-center gap-1 font-heading text-lg text-neutral-500 uppercase tracking-wide">
                            Unavailable
                          </span>
                        ) : (
                          <span className="mt-4 inline-flex items-center gap-1 font-heading text-lg text-neon-green uppercase tracking-wide">
                            <Plus size={16} /> Add
                          </span>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {/* Sticky cart bar (mobile) */}
      {count > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-black border-t border-neon-green/30 p-4 sm:hidden">
          <button onClick={() => setOpen(true)} className="w-full flex items-center justify-between font-heading text-xl bg-neon-green text-black px-5 py-3">
            <span>View Cart ({count})</span>
            <span>{money(totalCents)}</span>
          </button>
        </div>
      )}

      <ItemDialog
        item={active}
        open={!!active}
        onClose={() => setActive(null)}
        flavors={flavors}
        sodas={sodas}
        dips={dips}
        comboPrice={comboPrice}
      />
      <CartSheet />
    </div>
  );
}
